/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonStyled, TextStyled } from '@nike/nike-design-system-components';
import { func, bool, array, string } from 'prop-types';
import { Box, Flex } from 'rebass';

import Modal from '../../modal/Modal';
import BadgeStyled from '../../components/BadgeStyled';

import { getCatalogTypes } from './selectors';

import { bulkUpdateDepartmentCatalogTypes } from './actions';

const AddCatalogTypesModal = ({
  salesOrg,
  closeModal,
  isOpen,
  allDepartments,
  selectDepartmentIds,
  bulkSetDepartmentCatalogTypes
}) => {
  const dispatch = useDispatch();

  const [selectedCatalogTypeIds, setSelectedCatalogTypeIds] = useState([]);
  const [expandDepartments, setExpandDepartments] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [invalidNames, setInvalidNames] = useState([]);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const catalogTypes = useSelector(getCatalogTypes);

  const dropdownOptions = useMemo(() => {
    const selectedDepartments = allDepartments.filter(d => selectDepartmentIds.includes(d.id));
    return Object.values(catalogTypes)
      .filter(
        catalogType =>
          !!catalogType &&
          !selectedDepartments.every(dept =>
            dept.catalogTypes.find(deptCatalogType => deptCatalogType.id === catalogType.id)
          ) &&
          catalogType.salesOrg === salesOrg
      )
      .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
      .map(catalogType => catalogType.name);
  }, [JSON.stringify(allDepartments), selectDepartmentIds, catalogTypes, salesOrg]);

  const filteredOptions = useMemo(() => {
    const availableOptions = dropdownOptions.filter(
      name => !selectedCatalogTypeIds.includes(
        Object.values(catalogTypes).find(ct => ct.name === name)?.id
      )
    );
    if (inputValue) {
      const searchTerm = inputValue.toLowerCase();
      return availableOptions.filter(item => item.toLowerCase().indexOf(searchTerm) > -1);
    }
    return availableOptions;
  }, [inputValue, dropdownOptions, selectedCatalogTypeIds, catalogTypes]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedCatalogTypeIds([]);
      setInputValue('');
      setInvalidNames([]);
      setShowDropdown(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resolveCatalogTypeNames = names => {
    const catalogTypeValues = Object.values(catalogTypes);
    const validIds = [];
    const invalid = [];

    names.forEach(name => {
      const trimmed = name.trim();
      if (!trimmed) return;
      const match = catalogTypeValues.find(
        ct => ct.name.toLowerCase() === trimmed.toLowerCase() && ct.salesOrg === salesOrg
      );
      if (match && !selectedCatalogTypeIds.includes(match.id)) {
        validIds.push(match.id);
      } else if (!match) {
        invalid.push(trimmed);
      }
    });

    return { validIds, invalid };
  };

  const handleItemSelect = catalogTypeName => {
    const catalogTypeValues = Object.values(catalogTypes);
    const catalogType = catalogTypeValues.find(ct => ct.name === catalogTypeName);
    if (catalogType && !selectedCatalogTypeIds.includes(catalogType.id)) {
      setSelectedCatalogTypeIds([...selectedCatalogTypeIds, catalogType.id]);
    }
    setInputValue('');
    setShowDropdown(false);
    setInvalidNames([]);
  };

  const handleRemoveSelected = idToRemove => {
    setSelectedCatalogTypeIds(selectedCatalogTypeIds.filter(id => id !== idToRemove));
  };

  const handlePaste = event => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    setInputValue(pastedText);
    setShowDropdown(!pastedText.includes(','));
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter' && inputValue) {
      event.preventDefault();
      const names = inputValue.split(/[,\n]+/);
      const { validIds, invalid } = resolveCatalogTypeNames(names);

      if (validIds.length) {
        setSelectedCatalogTypeIds([...selectedCatalogTypeIds, ...validIds]);
      }
      if (invalid.length) {
        setInvalidNames(invalid);
      } else {
        setInvalidNames([]);
      }
      setInputValue('');
      setShowDropdown(false);
    }
  };

  const handleInputChange = event => {
    setInputValue(event.target.value);
    setShowDropdown(true);
    setInvalidNames([]);
  };

  const handleAddCatalogTypeToDepartment = async () => {
    const departments = allDepartments.filter(d => selectDepartmentIds.includes(d.id));
    const catalogTypesToAdd = selectedCatalogTypeIds.map(id => catalogTypes[id]).filter(Boolean);

    const departmentIdToCatalogTypes = departments.reduce(
      (departmentToCatalogTypes, department) => ({
        ...departmentToCatalogTypes,
        [department.id]: [
          ...department.catalogTypes,
          ...catalogTypesToAdd.filter(
            ctToAdd => !department.catalogTypes.find(ct => ct.id === ctToAdd.id)
          )
        ]
      }),
      {}
    );

    await dispatch(bulkUpdateDepartmentCatalogTypes({ departmentIdToCatalogTypes }));
    bulkSetDepartmentCatalogTypes(departmentIdToCatalogTypes);
  };

  const content = () => {
    const departmentNames = [
      ...new Set(selectDepartmentIds.map(id => allDepartments.find(d => d.id === id).name))
    ].sort();

    const hasOverflow = departmentNames.length > 12;

    return (
      <Box width='100%' minHeight='350px'>
        <div>
          <TextStyled appearance='title4' css={{ paddingBottom: '5px' }}>
            Selected Departments:
          </TextStyled>
          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              height: expandDepartments || !hasOverflow ? 'unset' : '120px',
              overflowY: expandDepartments ? 'visible' : 'hidden'
            }}
          >
            {departmentNames.map(dept => (
              <TextStyled key={dept} appearance='body2'>{dept}</TextStyled>
            ))}
          </Box>
          {hasOverflow && (
            <ButtonStyled
              appearance='text'
              size='small'
              onClick={() => setExpandDepartments(!expandDepartments)}
              css={{
                float: 'right'
              }}
            >
              {expandDepartments ? '...Show Less' : '...Show More'}
            </ButtonStyled>
          )}
        </div>

        {!salesOrg ? (
          <TextStyled appearance='body2' color='error'>
            All Selected Departments must be in the same Sales Org to add a Catalog Type.
          </TextStyled>
        ) : null}

        <Box css={{ position: 'relative', marginTop: '10px' }}>
          <label htmlFor='addCatalogTypeField'>
            <TextStyled appearance='body1Strong' css={{ marginBottom: '4px' }}>
              Catalog Type
            </TextStyled>
          </label>
          <input
            id='addCatalogTypeField'
            ref={inputRef}
            role='combobox'
            aria-label='Catalog Type'
            aria-expanded={showDropdown && filteredOptions.length > 0}
            aria-controls='addCatalogTypeField-listbox'
            aria-autocomplete='list'
            data-testid='addCatalogTypeField'
            type='text'
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => {
              setExpandDepartments(false);
              setShowDropdown(true);
            }}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            placeholder='Search or paste catalog types...'
            css={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              '&:focus': {
                outline: 'none',
                borderColor: '#111'
              }
            }}
          />

          {showDropdown && filteredOptions.length > 0 && (
            <Box
              id='addCatalogTypeField-listbox'
              role='listbox'
              ref={dropdownRef}
              css={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                maxHeight: '150px',
                overflowY: 'auto',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: '#fff',
                zIndex: 10,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              {filteredOptions.map(option => (
                <Box
                  key={option}
                  onClick={() => handleItemSelect(option)}
                  css={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                >
                  <TextStyled appearance='body2'>{option}</TextStyled>
                </Box>
              ))}
            </Box>
          )}

          {showDropdown && inputValue && filteredOptions.length === 0 && (
            <Box
              css={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: '#fff',
                zIndex: 10
              }}
            >
              <TextStyled appearance='body2'>No matches found.</TextStyled>
            </Box>
          )}
        </Box>

        {selectedCatalogTypeIds.length > 0 && (
          <Flex flexWrap='wrap' css={{ marginTop: '10px', gap: '4px' }}>
            {selectedCatalogTypeIds.map(id => {
              const ct = catalogTypes[id];
              return ct ? (
                <BadgeStyled
                  key={id}
                  text={ct.name}
                  onClose={() => handleRemoveSelected(id)}
                  backgroundColor='#e5e5e5'
                />
              ) : null;
            })}
          </Flex>
        )}

        {invalidNames.length > 0 && (
          <Box css={{ marginTop: '8px' }}>
            <TextStyled appearance='body2' css={{ color: '#d32f2f' }}>
              The following catalog types were not found:
            </TextStyled>
            {invalidNames.map(name => (
              <TextStyled key={name} appearance='body2' css={{ color: '#d32f2f', paddingLeft: '8px' }}>
                {name}
              </TextStyled>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const footer = ({ isLoading, toggleIsLoading }) => {
    const isSubmitButtonDisabled = isLoading || selectedCatalogTypeIds.length === 0;

    const handleSubmit = async () => {
      toggleIsLoading(true, 'Adding Catalog Type...');

      await handleAddCatalogTypeToDepartment();

      toggleIsLoading(false);

      closeModal();
    };

    return (
      <ButtonStyled
        data-testid='addProducts-submit-button'
        type='button'
        appearance='primary'
        background='light'
        size='small'
        onClick={handleSubmit}
        disabled={isSubmitButtonDisabled}
      >
        Add
      </ButtonStyled>
    );
  };

  return (
    <Modal
      title='Add Catalog Type'
      content={content}
      footer={footer}
      isOpen={isOpen}
      closeModal={closeModal}
      unwrapped
    />
  );
};

AddCatalogTypesModal.propTypes = {
  closeModal: func.isRequired,
  isOpen: bool,
  salesOrg: string.isRequired,
  allDepartments: array,
  selectDepartmentIds: array,
  bulkSetDepartmentCatalogTypes: func.isRequired
};

AddCatalogTypesModal.defaultProps = {
  allDepartments: [],
  selectDepartmentIds: [],
  isOpen: false
};

export default AddCatalogTypesModal;
